import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useClientUiSettings } from '../../hooks/useClientUiSettings';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  Chip
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ListItemIcon from '@mui/material/ListItemIcon';
import { dashboardTheme } from './styles/dashboardTheme';
import NotificationBell from '../common/NotificationBell';
import featureFlags from '../../config/featureFlags';
import SupportTicketDialog from '../chat/SupportTicketDialog';
import { rawApiClient } from '../../services/api';

// Helper function to check roles
const hasRole = (userRole, roleToCheck) => {
  const roles = Array.isArray(userRole) ? userRole : [userRole];
  return roles.includes(roleToCheck);
};

/**
 * ClientHeader Component
 * Custom header designed specifically for the client interface
 * Features AIMHI branding and a more therapeutic, welcoming design
 */
const ClientHeader = ({ user, token, onLogout, onChangePassword, onDeleteAccount }) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  // Phones (ST-20261006-0002): the nav links, Help and the account button sat in one row
  // that does not wrap, so at ~390px everything after Chat fell off the right edge. Below
  // the sm breakpoint the links move into a menu and Help shows as its icon.
  const [navAnchorEl, setNavAnchorEl] = useState(null);
  const [supportTicketOpen, setSupportTicketOpen] = useState(false);
  // Resolved client UI settings (Meeting 3). showNavEmojis defaults to false on
  // new deployments and is opt-in per service group.
  const { showNavEmojis, focusModeEnabled, focusMode, setFocusMode } = useClientUiSettings();

  // When focus mode is on, hide the AppBar entirely. A small floating "exit"
  // button (rendered below the AppBar return) gives the user a way out, and
  // a keyboard shortcut (Esc) toggles it off — accessibility skip-link.
  React.useEffect(() => {
    if (!focusMode) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setFocusMode(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusMode, setFocusMode]);

  // Shared session summaries used to be shown only in the waiting area, which a
  // client leaves within seconds of approval (ST-20260918-0001). The link
  // appears once there is something to read, so clients who never get one do
  // not see an empty tab.
  const [hasSharedSummaries, setHasSharedSummaries] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!token) return undefined;
    rawApiClient.get('/portal/me/summaries')
      .then((res) => { if (!cancelled) setHasSharedSummaries((res.data?.summaries || []).length > 0); })
      .catch(() => { /* no link rather than an error in the header */ });
    return () => { cancelled = true; };
  }, [token]);

  if (focusMode) {
    return (
      <IconButton
        aria-label="Exit focus mode"
        onClick={() => setFocusMode(false)}
        sx={{
          position: 'fixed',
          top: 8,
          right: 8,
          zIndex: 2000,
          background: 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(0,0,0,0.12)',
          '&:hover': { background: '#fff' }
        }}
      >
        <FullscreenExitIcon />
      </IconButton>
    );
  }

  // Derive current page from route for support ticket context
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path.startsWith('/chat')) return 'chat';
    if (path.startsWith('/journal')) return 'journal';
    if (path.startsWith('/summaries')) return 'summaries';
    if (path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/parent')) return 'parent_dashboard';
    return 'unknown';
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if user has parent role (has parent_of or guardian_of relationship)
  // OR if they signed up via a parent onboarding flow (before their child signs up)
  const hasActiveParentRelationship = user?.relatedAccounts?.some(
    link => (link.relationshipType === 'parent_of' || link.relationshipType === 'guardian_of')
            && link.status === 'active'
  );

  // Check if user is a child (has child_of relationship) - children should never see Family menu
  // even if their invitation tag contains 'parent' (e.g., 'PARENT-ADOLESCENT-...')
  const isChildUser = user?.relatedAccounts?.some(
    link => link.relationshipType === 'child_of' && link.status === 'active'
  );

  // For parents who signed up but haven't linked their child yet
  // Only applies if they are NOT a child themselves
  const usedParentSignupFlow = !isChildUser && user?.signupSource?.tag?.toLowerCase().includes('parent');
  const isParentUser = hasActiveParentRelationship || usedParentSignupFlow;

  const navItems = [
    ...(featureFlags.NEW_DASHBOARD ? [{ path: '/dashboard', label: 'Dashboard', emoji: '🏠' }] : []),
    { path: '/chat', label: 'Chat', emoji: '💬' },
    { path: '/journal', label: 'Journal', emoji: '📝' },
    ...(hasSharedSummaries ? [{ path: '/summaries', label: 'Summaries', emoji: '📄' }] : []),
    ...(isParentUser ? [{ path: '/parent/dashboard', label: 'Family', emoji: '👨‍👩‍👧' }] : [])
  ];

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        background: '#ffffff',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}
    >
      <Toolbar sx={{ height: 70, px: { xs: 1, sm: 3 } }}>
        {/* Logo Section */}
        <RouterLink 
          to={featureFlags.NEW_DASHBOARD ? '/dashboard' : '/chat'}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            textDecoration: 'none',
            marginRight: '16px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logo */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <img 
                src={`${process.env.PUBLIC_URL}/logo-desktop.png`}
                srcSet={`${process.env.PUBLIC_URL}/logo-desktop.png 1x, ${process.env.PUBLIC_URL}/logo-desktop2x.png 2x`}
                alt="AIMHI Logo"
                style={{ 
                  height: '48px',
                  width: 'auto'
                }}
              />
            </Box>
            {/* Mobile logo */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <img 
                src={`${process.env.PUBLIC_URL}/logo-mobile.png`}
                srcSet={`${process.env.PUBLIC_URL}/logo-mobile.png 1x, ${process.env.PUBLIC_URL}/logo-mobile2x.png 2x`}
                alt="AIMHI Logo"
                // The image has wide empty margins; show only its centre so the header
                // has room for the bell, Help and the account button on a 390px phone.
                style={{
                  height: '40px',
                  width: '48px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            </Box>
          </Box>
        </RouterLink>

        {/* Phones: the links in a menu, so Help and the account button stay on screen */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexGrow: 1 }}>
          <Button
            aria-label="Open navigation"
            aria-controls="client-nav-menu"
            aria-haspopup="true"
            onClick={(e) => setNavAnchorEl(e.currentTarget)}
            startIcon={<MenuIcon />}
            sx={{ color: dashboardTheme.colors.primary.main, fontWeight: 600, textTransform: 'none', borderRadius: '12px' }}
          >
            {(navItems.find(item => isActive(item.path)) || {}).label || 'Menu'}
          </Button>
          <Menu
            id="client-nav-menu"
            anchorEl={navAnchorEl}
            open={Boolean(navAnchorEl)}
            onClose={() => setNavAnchorEl(null)}
          >
            {navItems.map(item => (
              <MenuItem
                key={item.path}
                component={RouterLink}
                to={item.path}
                selected={isActive(item.path)}
                onClick={() => setNavAnchorEl(null)}
              >
                {showNavEmojis && item.emoji && (
                  <span style={{ fontSize: '1.1rem', marginRight: 8 }} aria-hidden="true">{item.emoji}</span>
                )}
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Navigation Links */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, flexGrow: 1 }}>
          {navItems.map(item => (
            <Button
              key={item.path}
              component={RouterLink}
              to={item.path}
              sx={{
                color: isActive(item.path) ? dashboardTheme.colors.primary.main : '#64748b',
                fontWeight: isActive(item.path) ? 600 : 400,
                fontSize: '0.95rem',
                borderRadius: '12px',
                px: 2,
                py: 1,
                position: 'relative',
                transition: 'all 0.2s',
                '&:hover': {
                  background: dashboardTheme.colors.primary.main + '10',
                  color: dashboardTheme.colors.primary.main
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isActive(item.path) ? '60%' : '0%',
                  height: '3px',
                  background: dashboardTheme.colors.primary.gradient,
                  borderRadius: '3px 3px 0 0',
                  transition: 'width 0.3s'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {showNavEmojis && item.emoji && (
                  <span style={{ fontSize: '1.1rem' }} aria-hidden="true">{item.emoji}</span>
                )}
                <span>{item.label}</span>
              </Box>
            </Button>
          ))}
        </Box>

        {/* User Section */}
        {/* Tighter on phones: with the notification bell the avatar was pushed off a 390px screen. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 2 } }}>
          <NotificationBell token={token} color="#64748b" />
          {/* Help: icon only on phones */}
          <IconButton
            aria-label="Help"
            onClick={() => setSupportTicketOpen(true)}
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: '#64748b' }}
          >
            <HelpOutlineIcon />
          </IconButton>
          {/* Help Button */}
          <Button
            size="small"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setSupportTicketOpen(true)}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              color: '#64748b',
              fontWeight: 500,
              fontSize: '0.85rem',
              borderRadius: '12px',
              px: 1.5,
              textTransform: 'none',
              '&:hover': {
                background: dashboardTheme.colors.primary.main + '10',
                color: dashboardTheme.colors.primary.main
              }
            }}
          >
            Help
          </Button>

          {/* User welcome message */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b',
                fontWeight: 500
              }}
            >
              Welcome back,
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: dashboardTheme.colors.primary.main,
                fontWeight: 600
              }}
            >
              {user?.name || user?.email}
            </Typography>
          </Box>

          {/* User Avatar and Menu */}
          <IconButton
            size="large"
            aria-label="account menu"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            sx={{
              p: { xs: 0.5, sm: 1.5 },
              ml: { xs: 0.5, sm: 0 },
              background: dashboardTheme.colors.primary.main + '10',
              '&:hover': {
                background: dashboardTheme.colors.primary.main + '20'
              }
            }}
          >
            <Avatar 
              sx={{ 
                width: 36, 
                height: 36,
                background: dashboardTheme.colors.primary.gradient,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                mt: 1.5,
                borderRadius: '12px',
                minWidth: 200,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            {/* User info in menu */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            
            <Divider />
            
            <MenuItem
              onClick={() => {
                handleClose();
                onChangePassword();
              }}
            >
              <SettingsIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} />
              Change Password
            </MenuItem>

            {/* Focus mode toggle — only shown when the platform/service-group has
                enabled the gate. Hides the top nav until the user exits via the
                small fullscreen-exit button or by pressing Escape. */}
            {focusModeEnabled && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  setFocusMode(true);
                }}
              >
                <FullscreenIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} />
                Enter focus mode
              </MenuItem>
            )}

            {/* Role Switching Options */}
            {user && user.role && (hasRole(user.role, 'clinical_supervisor') || hasRole(user.role, 'therapist') || hasRole(user.role, 'admin')) && (
              <>
                <Divider />
                {hasRole(user.role, 'clinical_supervisor') && (
                  <MenuItem
                    component={RouterLink}
                    to="/clinical-supervisor/dashboard"
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <SwapHoriz fontSize="small" />
                    </ListItemIcon>
                    Switch to Clinical Supervisor View
                  </MenuItem>
                )}
                {hasRole(user.role, 'therapist') && (
                  <MenuItem
                    component={RouterLink}
                    to="/therapist/dashboard"
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <SwapHoriz fontSize="small" />
                    </ListItemIcon>
                    Switch to Therapist View
                  </MenuItem>
                )}
                {hasRole(user.role, 'admin') && (
                  <MenuItem
                    component={RouterLink}
                    to="/admin/dashboard"
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <SwapHoriz fontSize="small" />
                    </ListItemIcon>
                    Switch to Admin View
                  </MenuItem>
                )}
              </>
            )}

            <Divider />

            <MenuItem
              onClick={() => {
                handleClose();
                onLogout();
              }}
            >
              <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} />
              Logout
            </MenuItem>
            
            <Divider />
            
            <MenuItem
              onClick={() => { 
                handleClose();
                onDeleteAccount(); 
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
              Delete Account
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Support Ticket Dialog */}
      <SupportTicketDialog
        open={supportTicketOpen}
        onClose={() => setSupportTicketOpen(false)}
        token={token}
        currentPage={getCurrentPage()}
      />
    </AppBar>
  );
};

export default ClientHeader;